/**
 * OS Project Template API
 *
 * Global project templates accessible via /os/project-templates routes.
 * Uses baseUrl.OS instead of baseUrl.SHERPA, but shares the same types.
 */

import axios from 'axios';
import { baseUrl, HttpMethods } from '../../base';
import {
  normalizeBulkTdmPayload,
  normalizeDeleteTdmParams,
  normalizeProjectTemplateListParams,
  normalizeProjectTemplateTdmParams,
  normalizeSingleTdmPayload,
  normalizeTdmSortPayload,
} from '../../Sherpa/ProjectTemplate/wire';
import type {
  DeleteProjectTemplateService,
  DeleteProjectTemplateTdmService,
  GetProjectTemplateResponse,
  GetProjectTemplateService,
  GetProjectTemplatesResponse,
  GetProjectTemplatesService,
  GetProjectTemplateTdmService,
  IPatchProjectTemplateBulkTDMsService,
  IPutBulkProjectTemplateTdmService,
  IPutProjectTemplateTDMImportService,
  PatchProjectTemplateService,
  PatchProjectTemplateTdmService,
  PostNewProjectTemplateService,
  PostProjectTemplateTdmService,
  PutProjectTemplateTdmService,
} from './types';

const projectTemplateBasePath = `${baseUrl.OS}/project-templates`;

export async function getProjectTemplates({ params }: GetProjectTemplatesService) {
  const res = await axios.request<{ data: GetProjectTemplatesResponse }>({
    method: HttpMethods.GET,
    url: `${projectTemplateBasePath}`,
    params: normalizeProjectTemplateListParams(params),
  });
  return res.data;
}

export async function getProjectTemplate({ templateId }: GetProjectTemplateService) {
  const res = await axios.request<GetProjectTemplateResponse>({
    method: HttpMethods.GET,
    url: `${projectTemplateBasePath}/${templateId}`,
  });
  return res.data;
}

export async function postNewProjectTemplate({ data }: PostNewProjectTemplateService) {
  const res = await axios.request({
    method: HttpMethods.POST,
    url: `${projectTemplateBasePath}`,
    data,
  });

  return res.data;
}

export async function patchProjectTemplate({ templateId, data }: PatchProjectTemplateService) {
  const res = await axios.request({
    method: HttpMethods.PATCH,
    url: `${projectTemplateBasePath}/${templateId}`,
    data,
  });

  return res.data;
}

export async function deleteProjectTemplate({ templateId }: DeleteProjectTemplateService) {
  const res = await axios.request({
    method: HttpMethods.DELETE,
    url: `${projectTemplateBasePath}/${templateId}`,
  });
  return res.data;
}

export async function getProjectTemplateTdm({ templateId, params }: GetProjectTemplateTdmService) {
  const res = await axios.request<{ data: GetProjectTemplatesResponse }>({
    method: HttpMethods.GET,
    url: `${projectTemplateBasePath}/${templateId}/tdm`,
    params: normalizeProjectTemplateTdmParams(params),
  });
  return res.data;
}

export async function postProjectTemplateTdm({ templateId, data }: PostProjectTemplateTdmService) {
  const res = await axios.request({
    method: HttpMethods.POST,
    url: `${projectTemplateBasePath}/${templateId}/tdm`,
    data: normalizeSingleTdmPayload(data),
  });
  return res.data;
}

export async function putProjectTemplateTdm({ templateId, data }: PutProjectTemplateTdmService) {
  const res = await axios.request({
    method: HttpMethods.PUT,
    url: `${projectTemplateBasePath}/${templateId}/bulk-tdm`,
    data: normalizeBulkTdmPayload(data),
  });
  return res.data;
}

export async function deleteProjectTemplateTdm({ templateId, data, tdmId }: DeleteProjectTemplateTdmService) {
  const res = await axios.request({
    method: HttpMethods.DELETE,
    url: `${projectTemplateBasePath}/${templateId}/tdm/${tdmId}`,
    params: normalizeDeleteTdmParams(data),
  });
  return res.data;
}

export async function patchProjectTemplateTdm({ templateId, tdmId, data }: PatchProjectTemplateTdmService) {
  const res = await axios.request({
    method: HttpMethods.PATCH,
    url: `${projectTemplateBasePath}/${templateId}/tdm/${tdmId}`,
    data: normalizeSingleTdmPayload(data),
  });

  return res.data;
}

export async function putProjectTemplateTDMImport({ templateId, data }: IPutProjectTemplateTDMImportService) {
  const res = await axios.request({
    method: HttpMethods.PUT,
    url: `${projectTemplateBasePath}/${templateId}/bulk-tdm`,
    data: normalizeBulkTdmPayload(data),
  });

  return res.data;
}

export async function patchProjectTemplateBulkTDMs({ templateId, data }: IPatchProjectTemplateBulkTDMsService) {
  const res = await axios.request({
    method: HttpMethods.PATCH,
    url: `${projectTemplateBasePath}/${templateId}/tdm-sort`,
    data: normalizeTdmSortPayload(data),
  });

  return res.data;
}

export async function putBulkProjectTemplateTdm({ templateId, data }: IPutBulkProjectTemplateTdmService) {
  const res = await axios.request({
    method: HttpMethods.PUT,
    url: `${projectTemplateBasePath}/${templateId}/bulk-tdm`,
    data: normalizeBulkTdmPayload(data),
  });
  return res.data;
}
